import { Box, CircularProgress } from '@mui/material'
import { useContext, useEffect, useState } from 'react'

import {
  PatientsContext,
  PatientsContextInterface,
} from '../../../providers/Patients/Patients.provider'
import { useProviderDetails } from '../../../providers/ProviderDetails/ProviderDetails.provider'
import { useProviders } from '../../../providers/ProvidersProvider/ProvidersProvider.provider'
import {
  ProviderTaskBarrier,
  ProviderTaskContext,
  ProviderTaskContextInterface,
  ProviderTaskPriorityLevel,
  ProviderTaskResponse,
  ProviderTaskStatus,
  ProviderTaskType,
  ProviderType,
} from '../../../providers/ProviderTask/ProviderTask.provider'
import { ProviderTasksView } from './ProviderTasks.view'

const ProviderTasks = () => {
  const {
    providerTasks,
    providerTaskHistory,
    getProviderTaskTypes,
    getProviderTaskPriorityLevels,
    getProviderTaskStatuses,
    getProviderTaskBarriers,
    getAllProviderTasks,
    getProviderTypes,
    getProviderTasksByProviderId,
    createProviderTaskType,
    updateProviderTaskType,
    createProviderTask,
    updateProviderTask,
    deleteProviderTask,
    addProviderTaskComment,
    editProviderTaskComment,
    deleteProviderTaskComment,
    getProviderTaskHistory,
  } = useContext(ProviderTaskContext) as ProviderTaskContextInterface

  const { myProviderDetails } = useProviderDetails()

  const { allActivePatients, getListOfAllActivePatients } = useContext(
    PatientsContext
  ) as PatientsContextInterface

  const { providers, getAllProviders } = useProviders()

  const [taskTypes, setTaskTypes] = useState<ProviderTaskType[]>([])

  const [providerTypes, setProviderTypes] = useState<ProviderType[]>([])

  const [priorityLevels, setPriorityLevels] = useState<
    ProviderTaskPriorityLevel[]
  >([])
  const [statuses, setStatuses] = useState<ProviderTaskStatus[]>([])
  const [barriers, setBarriers] = useState<ProviderTaskBarrier[]>([])
  const [loading, setLoading] = useState(true)

  const [optimisticTasks, setOptimisticTasks] = useState<
    ProviderTaskResponse[]
  >([])

  const isAdmin = myProviderDetails?.accessRoles?.some(
    (role) =>
      role.description.includes('Admin') ||
      role.description.includes('SuperAdmin')
  )

  const loadData = async () => {
    setLoading(true)

    const [types, providerTypes, levels, statusList, barrierList] =
      await Promise.all([
        getProviderTaskTypes(),
        getProviderTypes(),
        getProviderTaskPriorityLevels(),
        getProviderTaskStatuses(),
        getProviderTaskBarriers(),
      ])

    if (types) setTaskTypes(types)
    if (providerTypes) setProviderTypes(providerTypes)
    if (levels) setPriorityLevels(levels)
    if (statusList) setStatuses(statusList)
    if (barrierList) setBarriers(barrierList)

    await getListOfAllActivePatients()
    await getAllProviders()

    if (myProviderDetails?.providerId) {
      await getProviderTasksByProviderId(myProviderDetails?.providerId)
    }

    setLoading(false)
  }

  useEffect(() => {
    loadData()
  }, [])

  // make a copy of tasks, so that we can update local state later to reflect changes fast
  useEffect(() => {
    setOptimisticTasks(providerTasks ?? [])
  }, [providerTasks])

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: '100%',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <ProviderTasksView
      isAdmin={!!isAdmin}
      tasks={optimisticTasks}
      setOptimisticTasks={setOptimisticTasks}
      providers={providers?.filter((p) => p.isActive) ?? []}
      allActivePatients={allActivePatients}
      myProviderId={myProviderDetails?.providerId ?? 0}
      taskTypes={taskTypes}
      providerTypes={providerTypes}
      setTaskTypes={setTaskTypes}
      priorityLevels={priorityLevels}
      statuses={statuses}
      barriers={barriers}
      getAllProviderTasks={getAllProviderTasks}
      getProviderTasksByProviderId={getProviderTasksByProviderId}
      onCreateTask={createProviderTask}
      onUpdateTask={updateProviderTask}
      onDeleteTask={deleteProviderTask}
      onCreateTaskType={createProviderTaskType}
      onUpdateTaskType={updateProviderTaskType}
      onAddProviderTaskComment={addProviderTaskComment}
      onEditProviderTaskComment={editProviderTaskComment}
      onDeleteProviderTaskComment={deleteProviderTaskComment}
      providerTaskHistory={providerTaskHistory}
      onFetchProviderTaskHistory={getProviderTaskHistory}
    />
  )
}

export { ProviderTasks }
